import { BottomSheet } from '@/shared/ui';
import type { EventLog } from '@/shared/types';
import { Copy, Terminal, Layers, AlertTriangle } from 'lucide-react';
import { haptic } from '@/shared/utils/telegram';

interface EventDetailSheetProps {
  event: EventLog | null;
  open: boolean;
  onClose: () => void;
}

export function EventDetailSheet({ event, open, onClose }: EventDetailSheetProps) {
  if (!event) return null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    haptic.notification('success');
  };

  const copyAllData = () => {
    copyToClipboard(JSON.stringify(event, null, 2));
  };

  // Display subid as a single entry if it exists
  const hasSubId = event.subid && event.subid.trim() !== '';

  return (
    <BottomSheet isOpen={open} onClose={onClose} title="Event Diagnostics">
      <>
        {/* ID Grid */}
        <div className="grid grid-cols-1 gap-3">
          {event.externalUid && (
            <div className="flex flex-col gap-1 p-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-lg">
              <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">External UID</span>
              <span className="text-sm font-mono break-all">{event.externalUid}</span>
            </div>
          )}
          <div className="flex flex-col gap-1 p-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-lg">
            <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">Internal Log ID</span>
            <span className="text-sm font-mono break-all">{event.id}</span>
          </div>
          {event.transactionId && (
            <div className="flex flex-col gap-1 p-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-lg">
              <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">Transaction ID</span>
              <span className="text-sm font-mono break-all">{event.transactionId}</span>
            </div>
          )}
          {event.affiliateId && (
            <div className="flex flex-col gap-1 p-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-lg">
              <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">Affiliate ID</span>
              <span className="text-sm font-mono break-all">{event.affiliateId}</span>
            </div>
          )}
          {event.trackingTag && (
            <div className="flex flex-col gap-1 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <span className="text-xs text-amber-600 font-medium">Tracking Tag</span>
              <span className="text-sm font-mono break-all text-amber-600">{event.trackingTag}</span>
            </div>
          )}
          {event.trafficSource && (
            <div className="flex flex-col gap-1 p-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-lg">
              <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">Traffic Source</span>
              <span className="text-sm font-mono break-all">{event.trafficSource}</span>
            </div>
          )}
        </div>

        {/* Sub ID */}
        {hasSubId && (
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-bold">
              <Layers size={12} />
              Sub ID
            </h4>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 bg-[var(--tg-theme-secondary-bg-color)] rounded-md font-mono">
                {event.subid}
              </span>
            </div>
          </div>
        )}

        {/* Raw Data */}
        {event.raw && (
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-bold">
              <Terminal size={12} />
              Raw Data
            </h4>
            <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-lg p-3 overflow-x-auto">
              <pre className="text-xs font-mono">
                {typeof event.raw === 'string' ? event.raw : JSON.stringify(event.raw, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Duplicate Warning */}
        {event.isDuplicate && (
          <div className="flex gap-3 p-4 bg-rose-500/10 border border-rose-500/20 rounded-lg">
            <AlertTriangle size={20} className="text-rose-600 flex-shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-bold text-rose-600">Duplicate Detected</span>
              <span className="text-sm text-[var(--tg-theme-hint-color)]">
                This event was already processed and marked as duplicate.
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            className="flex-1 py-3 px-4 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] font-bold text-sm active:scale-95 transition-transform border border-black/5 dark:border-white/5 flex items-center justify-center gap-2"
            onClick={() => {
              haptic.selection();
              copyAllData();
            }}
          >
            <Copy size={16} />
            Copy All
          </button>
          <button
            className="flex-1 py-3 px-4 rounded-xl bg-[var(--tg-theme-link-color)] text-white font-bold text-sm active:scale-95 transition-transform shadow-lg"
            onClick={() => {
              haptic.impact('light');
              onClose();
            }}
          >
            Dismiss
          </button>
        </div>
      </>
    </BottomSheet>
  );
}
